import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Stage, StageEvents, SubscribeType } from 'amazon-ivs-web-broadcast';
import './ViewerCamOverlay.css';

const SUBSCRIBE_STRATEGY = {
  stageStreamsToPublish: () => [],
  shouldPublishParticipant: () => false,
  shouldSubscribeToParticipant: (participant) =>
    participant.isLocal ? SubscribeType.NONE : SubscribeType.AUDIO_VIDEO,
};

function ViewerTile({ stream, username }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  return (
    <div className="vco-tile">
      <video ref={videoRef} className="vco-tile__video" autoPlay playsInline muted={false} />
      <span className="vco-tile__name">{username}</span>
    </div>
  );
}

export default function ViewerCamOverlay() {
  const [viewers, setViewers] = useState([]);
  const stageRef = useRef(null);
  const participantMediaRef = useRef(new Map());

  useEffect(() => {
    const { style: html } = document.documentElement;
    const { style: body } = document.body;
    html.overflow = 'hidden';
    html.background = 'transparent';
    body.overflow = 'hidden';
    body.background = 'transparent';
    return () => {
      html.overflow = '';
      html.background = '';
      body.overflow = '';
      body.background = '';
    };
  }, []);

  const params = new URLSearchParams(window.location.search);
  const apiUrl = params.get('api_url') || '';
  const token = params.get('token') || '';
  const userId = params.get('user_id') || '';

  const notifyViewers = useCallback(() => {
    setViewers(
      Array.from(participantMediaRef.current.values()).filter((v) => v.stream.getTracks().length > 0)
    );
  }, []);

  const joinStage = useCallback(async () => {
    if (stageRef.current || !apiUrl || !token || !userId) return;

    try {
      const res = await fetch(`${apiUrl}/ad-push/ivs-token/streamer`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const { token: stageToken } = await res.json();

      const stage = new Stage(stageToken, SUBSCRIBE_STRATEGY);

      stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED, (participant, streams) => {
        if (participant.isLocal) return;
        const username = participant.attributes?.username || participant.attributes?.identity || 'Viewer';
        let entry = participantMediaRef.current.get(participant.id);
        if (!entry) {
          entry = { stream: new MediaStream(), username };
          participantMediaRef.current.set(participant.id, entry);
        }
        streams.forEach((s) => {
          try { entry.stream.addTrack(s.mediaStreamTrack); } catch {}
        });
        notifyViewers();
      });

      stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_REMOVED, (participant, streams) => {
        const entry = participantMediaRef.current.get(participant.id);
        if (entry) {
          streams.forEach((s) => {
            try { entry.stream.removeTrack(s.mediaStreamTrack); } catch {}
          });
          notifyViewers();
        }
      });

      stage.on(StageEvents.STAGE_PARTICIPANT_LEFT, (participant) => {
        participantMediaRef.current.delete(participant.id);
        notifyViewers();
      });

      await stage.join();
      stageRef.current = stage;
    } catch {}
  }, [apiUrl, token, userId, notifyViewers]);

  useEffect(() => {
    joinStage();
    return () => {
      if (stageRef.current) {
        try { stageRef.current.leave(); } catch {}
        stageRef.current = null;
      }
    };
  }, [joinStage]);

  if (viewers.length === 0) return null;

  return (
    <div className="vco-container">
      {viewers.map((v, i) => (
        <ViewerTile key={i} stream={v.stream} username={v.username} />
      ))}
    </div>
  );
}
